import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSeedling, faHandsHelping, faTractor, faBuilding, faHeadset, faLeaf } from '@fortawesome/free-solid-svg-icons';
import './services.css';

const Services = () => {

    const refElements = useRef([]);

    const addToRefs = (el) => {
        if (el && !refElements.current.includes(el)) {
            refElements.current.push(el);
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                }
            });
        }, { threshold: 0.15 });

        refElements.current.forEach(ref => {
            if (ref) observer.observe(ref);
        });

        return () => {
            refElements.current.forEach(ref => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, []);

    return (
        <div className='sp-page-wrapper'>

            {/* Hero Section */}
            <section className="sp-hero">
                <div className="sp-hero-content">
                    <span className="sp-hero-badge">Amaravathi Naturo Tech pvt ltd</span>
                    <h1>Expert Land Management<br />For Sustainable Yields</h1>
                    <div className="sp-hero-shape"></div>
                </div>
            </section>

            {/* Overview Cards */}
            <section className="sp-overview-section">
                <div className="sp-grid">
                    <div className="sp-card" ref={addToRefs}>
                        <div className="sp-icon-wrapper">
                            <FontAwesomeIcon icon={faHandsHelping} />
                        </div>
                        <h3>Commercial Farming</h3>
                        <p>End-to-end management for large-scale crop production, livestock, and technology integration.</p>
                    </div>
                    <div className="sp-card" ref={addToRefs}>
                        <div className="sp-icon-wrapper">
                            <FontAwesomeIcon icon={faSeedling} />
                        </div>
                        <h3>Organic Farming</h3>
                        <p>Certified organic produce grown through regenerative practices and farm-to-table partnerships.</p>
                    </div>
                    <div className="sp-card" ref={addToRefs}>
                        <div className="sp-icon-wrapper">
                            <FontAwesomeIcon icon={faBuilding} />
                        </div>
                        <h3>Farm Development</h3>
                        <p>Complete land assessment, planning, infrastructure design, and automation implementation.</p>
                    </div>
                    <div className="sp-card" ref={addToRefs}>
                        <div className="sp-icon-wrapper">
                            <FontAwesomeIcon icon={faHeadset} />
                        </div>
                        <h3>Consulting & Training</h3>
                        <p>Expert guidance, workshops, and sustainability audits for aspiring and established farmers.</p>
                    </div>
                </div>
            </section>

            {/* Detailed Content Sections */}
            <section className="sp-detail-section">

                {/* 1. Commercial Farming */}
                <div className="sp-detail-row">
                    <div className="sp-visual-wrapper" ref={addToRefs}>
                        <div className="sp-img-frame">
                            <img src="https://res.cloudinary.com/drevfgyks/image/upload/v1725018118/AMARAVATHI%20NATURO%20TECH%20PVT%20LTD/business-agreement-shaking-hands-melon-plantation_brljhx.jpg" alt="Commercial Farming" />
                        </div>
                        <div className="sp-accent-blob bottom-left"></div>
                    </div>
                    <div className="sp-detail-content" ref={addToRefs}>
                        <span className="sp-section-title">Large Scale Operations</span>
                        <h2>Commercial Farming</h2>
                        <p className="sp-desc-lead">
                            At Amaravathi Naturo Tech, we specialize in large-scale commercial farming operations that meet the demands of global markets. Our experienced team utilizes advanced farming techniques and best practices to ensure high yields.
                        </p>
                        <ul className="sp-feature-list">
                            <li><strong>Crop Production:</strong> Cultivating grains, fruits, vegetables, and legumes for retailers.</li>
                            <li><strong>Livestock Farming:</strong> Premium dairy, poultry, and meat through ethical practices.</li>
                            <li><strong>Technology Integration:</strong> Precision farming, GPS tracking, and AI-powered analysis.</li>
                            <li><strong>Supply Chain Management:</strong> Farm-to-market quality assurance.</li>
                        </ul>
                    </div>
                </div>

                {/* 2. Organic Farming */}
                <div className="sp-detail-row reverse">
                    <div className="sp-visual-wrapper" ref={addToRefs}>
                        <div className="sp-img-frame">
                            <img src="https://res.cloudinary.com/drevfgyks/image/upload/v1725213502/AMARAVATHI%20NATURO%20TECH%20PVT%20LTD/Screenshot_2024-09-01_215437_o6hx7m.png" alt="Organic Farming" />
                        </div>
                        <div className="sp-accent-blob top-right"></div>
                    </div>
                    <div className="sp-detail-content" ref={addToRefs}>
                        <span className="sp-section-title">Sustainable & Pure</span>
                        <h2>Organic Farming</h2>
                        <p className="sp-desc-lead">
                            Our organic farming division prioritizes the long-term health of the soil, biodiversity, and the local ecosystem, growing healthy, chemical-free produce.
                        </p>
                        <ul className="sp-feature-list">
                            <li><strong>Certified Organic:</strong> 100% organic fruits, vegetables, and grains.</li>
                            <li><strong>Sustainable Practices:</strong> Enhancing soil fertility and preserving resources.</li>
                            <li><strong>Farm-to-Table:</strong> Fresh produce directly to local markets and restaurants.</li>
                            <li><strong>Regenerative Agriculture:</strong> Restoring soil health and sequestering carbon.</li>
                        </ul>
                    </div>
                </div>

                {/* 3. Consulting & Training */}
                <div className="sp-detail-row">
                    <div className="sp-visual-wrapper" ref={addToRefs}>
                        <div className="sp-img-frame">
                            <img src="https://res.cloudinary.com/drevfgyks/image/upload/v1725001353/AMARAVATHI%20NATURO%20TECH%20PVT%20LTD/young-farmer-taking-care-his-business_ygqchv.jpg" alt="Consulting" />
                        </div>
                        <div className="sp-accent-blob bottom-left"></div>
                    </div>
                    <div className="sp-detail-content" ref={addToRefs}>
                        <span className="sp-section-title">Knowledge Sharing</span>
                        <h2>Consulting & Training</h2>
                        <p className="sp-desc-lead">
                            We offer expert consulting services and training programs for aspiring farmers, helping them transition to more sustainable and efficient practices.
                        </p>
                        <ul className="sp-feature-list">
                            <li><strong>Farm Management:</strong> Optimizing operations for efficiency and profit.</li>
                            <li><strong>Organic Workshops:</strong> Hands-on training in sustainable growing.</li>
                            <li><strong>Sustainability Audits:</strong> Evaluating and improving eco-friendly practices.</li>
                        </ul>
                    </div>
                </div>

                {/* 4. Farm Development */}
                <div className="sp-detail-row reverse">
                    <div className="sp-visual-wrapper" ref={addToRefs}>
                        <div className="sp-img-frame">
                            <img src="https://res.cloudinary.com/drevfgyks/image/upload/v1723378843/AMARAVATHI%20NATURO%20TECH%20PVT%20LTD/woman-man-laboratory-robes-examine-carefully-plants-greenhouse_bnwsre.jpg" alt="Development" />
                        </div>
                        <div className="sp-accent-blob top-right"></div>
                    </div>
                    <div className="sp-detail-content" ref={addToRefs}>
                        <span className="sp-section-title">Turnkey Solutions</span>
                        <h2>Farm Development</h2>
                        <p className="sp-desc-lead">
                            For investors and entrepreneurs, we provide complete farm development solutions, from land acquisition to full-scale establishment.
                        </p>
                        <ul className="sp-feature-list">
                            <li><strong>Land Assessment:</strong> Soil and climate evaluation for optimal crops.</li>
                            <li><strong>Design & Construction:</strong> Modern, efficient farm infrastructure.</li>
                            <li><strong>Automation:</strong> Smart irrigation, sensors, and monitoring systems.</li>
                        </ul>
                    </div>
                </div>

            </section>

            <div className="sp-footer-cta">
                <p className="sp-cta-text">Emerging Farmers Empowerment</p>
            </div>

        </div>
    );
};

export default Services;
